import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import {
  onIdTokenChanged, signOut, createUserWithEmailAndPassword,
  signInWithEmailAndPassword, updateProfile, signInWithCustomToken, User,
} from 'firebase/auth';
import { initializeUser } from '../services/api';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';
const CANVAS_APP_ID = 'lexquant';

interface UserProfile { display_name?: string; email?: string; jurisdiction?: string; [key: string]: any; }
interface BridgeTokenInfo { status: 'existing_account' | 'new_account'; email: string; display_name?: string; message: string; }

interface AuthContextType {
  authLoading: boolean;
  currentUser: User | null;
  userProfile: UserProfile | null;
  signUp: (email: string, password: string, displayName: string) => Promise<any>;
  logIn: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  bridgeNeedsPassword: boolean;
  bridgeTokenInfo: BridgeTokenInfo | null;
  bridgeProcessing: boolean;
  bridgeError: string | null;
  completeBridgeLogin: (password: string) => Promise<boolean>;
  cancelBridgeLogin: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [bridgeNeedsPassword, setBridgeNeedsPassword] = useState(false);
  const [bridgeTokenInfo, setBridgeTokenInfo] = useState<BridgeTokenInfo | null>(null);
  const [bridgeToken, setBridgeToken] = useState<string | null>(null);
  const [bridgeProcessing, setBridgeProcessing] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);

  useEffect(() => {
    return onIdTokenChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) { setUserProfile(null); setAuthLoading(false); }
    });
  }, []);

  // Handle bridge token in URL
  useEffect(() => {
    const handleBridge = async () => {
      const token = new URLSearchParams(window.location.search).get('talkoru_token');
      if (!token) return;
      setBridgeToken(token);
      setBridgeProcessing(true);
      try {
        const res = await fetch(`${API_URL}/api/bridge/check_token`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) { const e = await res.json(); setBridgeError(e.error || 'Token invalid'); cleanUrl(); return; }
        const data = await res.json();
        setBridgeTokenInfo(data);
        setBridgeNeedsPassword(true);
      } catch (e: any) {
        setBridgeError(e.message);
      } finally {
        setBridgeProcessing(false);
      }
    };
    handleBridge();
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, `artifacts/${CANVAS_APP_ID}/users/${currentUser.uid}`);
    getDoc(ref).then((snap) => { setUserProfile(snap.exists() ? snap.data() as UserProfile : null); setAuthLoading(false); }).catch(() => setAuthLoading(false));
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || authLoading) return;
    const ref = doc(db, `artifacts/${CANVAS_APP_ID}/users/${currentUser.uid}`);
    return onSnapshot(ref, (snap) => { if (snap.exists()) setUserProfile(snap.data() as UserProfile); });
  }, [currentUser, authLoading]);

  function cleanUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('talkoru_token');
    window.history.replaceState({}, '', url.toString());
  }

  const completeBridgeLogin = async (password: string): Promise<boolean> => {
    if (!bridgeToken || !bridgeTokenInfo) return false;
    setBridgeProcessing(true); setBridgeError(null);
    try {
      const res = await fetch(`${API_URL}/api/bridge/generate_firebase_token`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: bridgeToken, password, is_new_account: bridgeTokenInfo.status === 'new_account' }),
      });
      if (!res.ok) { const e = await res.json(); setBridgeError(e.error || 'Login failed'); return false; }
      const data = await res.json();
      if (data.firebase_token) {
        await signInWithCustomToken(auth, data.firebase_token);
        cleanUrl();
        setBridgeNeedsPassword(false); setBridgeTokenInfo(null); setBridgeToken(null);
        toast.success('Welcome to LexQuant!');
        return true;
      }
      return false;
    } catch (e: any) { setBridgeError(e.message); return false; }
    finally { setBridgeProcessing(false); }
  };

  const cancelBridgeLogin = () => {
    cleanUrl();
    setBridgeNeedsPassword(false); setBridgeTokenInfo(null); setBridgeToken(null); setBridgeError(null);
  };

  async function signUp(email: string, password: string, displayName: string) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    const token = await cred.user.getIdToken();
    await initializeUser(token, displayName);
    return cred;
  }

  return (
    <AuthContext.Provider value={{
      authLoading, currentUser, userProfile,
      signUp, logIn: (e, p) => signInWithEmailAndPassword(auth, e, p), logout: () => signOut(auth),
      bridgeNeedsPassword, bridgeTokenInfo, bridgeProcessing, bridgeError, completeBridgeLogin, cancelBridgeLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
